import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

/** Auto-dismiss for in-app popup cards unless a custom ttlMs is passed. */
export const POPUP_TTL_MS = 3500;

const PopupContext = createContext(null);

export function PopupProvider({ children }) {
  const [popups, setPopups] = useState([]);

  const dismissPopup = useCallback((id) => {
    setPopups((prev) => prev.filter((popup) => popup.id !== id));
  }, []);

  const showPopup = useCallback((popup) => {
    const id = popup?.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const ttlMs = typeof popup?.ttlMs === "number" ? popup.ttlMs : POPUP_TTL_MS;
    const dedupeKey = popup?.title || popup?.message || id;

    setPopups((prev) => {
      const now = Date.now();
      const isDuplicate = prev.some(
        (p) => p.dedupeKey === dedupeKey && now - (p.createdAt || 0) < 10000
      );
      if (isDuplicate) return prev;
      const next = [...prev, { ...popup, id, dedupeKey, createdAt: now }];
      return next.length > 2 ? next.slice(-2) : next;
    });

    if (ttlMs > 0) {
      setTimeout(() => dismissPopup(id), ttlMs);
    }
  }, [dismissPopup]);

  const value = useMemo(
    () => ({
      popups,
      showPopup,
      dismissPopup
    }),
    [popups, showPopup, dismissPopup]
  );

  return <PopupContext.Provider value={value}>{children}</PopupContext.Provider>;
}

export function usePopup() {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error("usePopup must be used within PopupProvider");
  }
  return context;
}
